import { Loader2 } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { translationStatus } from "@/lib/localize";

/**
 * A tiny pill that shows the bilingual status of a journal article.
 *
 *   • both        — EN + NL are both filled                     → green
 *   • en_only     — only English populated (NL missing)         → gold
 *   • nl_only     — only Dutch populated (EN missing)           → gold
 *   • pending_nl  — auto-translate background task in progress  → amber + spinner
 *   • failed_nl   — auto-translate errored, retry candidate     → red
 *   • none        — no title yet                                → red
 *
 * Priority: persisted `article.translation_status` (set by backend) beats
 * the client-side content-derived value from `translationStatus()`. This
 * keeps the badge honest during the ~1-2 minute window while the AutoSEO
 * auto-translate background task is running (content shows en_only but
 * status is pending_nl).
 *
 * Used only in the Admin / Founder Portal. Never rendered on the public site.
 */
export default function TranslationBadge({ article, size = "sm", className = "" }) {
  const { t } = useT();
  const persisted = article?.translation_status;
  const derived = translationStatus(article);
  // Sticky states from the backend (pending / failed) override the
  // content-derived value; everything else falls through to the derived
  // status so legacy docs still render a sensible badge.
  const status = persisted === "pending_nl" || persisted === "failed_nl" ? persisted : derived;

  const map = {
    both: {
      label: t("admin.journal.trans.both"),
      cls: "border-emerald-400/40 text-emerald-300/90 bg-emerald-400/5",
    },
    en_only: {
      label: t("admin.journal.trans.en_only"),
      cls: "border-gold/50 text-gold bg-gold/5",
    },
    nl_only: {
      label: t("admin.journal.trans.nl_only"),
      cls: "border-gold/50 text-gold bg-gold/5",
    },
    pending_nl: {
      label: t("admin.journal.trans.pending_nl"),
      cls: "border-amber-400/50 text-amber-300 bg-amber-400/5",
      spinner: true,
    },
    failed_nl: {
      label: t("admin.journal.trans.failed_nl"),
      cls: "border-red-400/60 text-red-300 bg-red-400/10",
    },
    none: {
      label: t("admin.journal.trans.none"),
      cls: "border-red-400/50 text-red-300/90 bg-red-400/5",
    },
  };
  const cfg = map[status] || map.none;
  const pad = size === "sm" ? "px-1.5 py-0.5" : "px-2 py-1";
  const text = size === "sm" ? "text-[9px]" : "text-[10px]";

  const tooltip = status === "failed_nl" && article?.translation_error
    ? article.translation_error
    : t("admin.journal.trans.tooltip");

  return (
    <span
      data-testid={`translation-badge-${status}`}
      className={`inline-flex items-center gap-1 border ${pad} ${text} tracking-[0.24em] uppercase whitespace-nowrap ${cfg.cls} ${className}`}
      title={tooltip}
    >
      {cfg.spinner && <Loader2 size={9} strokeWidth={2.25} className="animate-spin" />}
      {cfg.label}
    </span>
  );
}
